/**
 * SelectAddress — Summary card only (shown on main checkout page)
 * Tapping "Change / Add address" sets isaddressSelectOpen=true
 * which triggers the parent to render AddressManager full screen
 */

import React, { useEffect, useState, useCallback } from "react";
import { EnvironmentOutlined, RightOutlined } from "@ant-design/icons";
import { Card, Col, Row, Skeleton, Space, Tag, App } from "antd";
import { addressAPI } from "../../api/services";

const ADDRESS_TYPE_LABELS = {
  home:    "Home",
  work:    "Work",
  other:   "Other",
  friends: "Friends / Family",
};

const SelectAddress = ({
  address,
  setAddress,
  onManageAddress,   // ← called when user wants to open address manager
  setAppLoader,
}) => {
  const { notification } = App.useApp();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading]     = useState(true);

  const fetchAddresses = useCallback(async () => {
    setLoading(true);
    try {
      const res    = await addressAPI.getAll();
      const raw    = res?.data?.data ?? res?.data ?? res ?? [];
      const active = (Array.isArray(raw) ? raw : []).filter((a) => a?.status === "active");
      setAddresses(active);
      return active;
    } catch (err) {
      if (err?.response?.status === 403) { localStorage.clear(); window.location.reload(); }
      notification.error({ message: err?.message || "Failed to load addresses" });
      return [];
    } finally {
      setLoading(false);
    }
  }, [notification]);

  useEffect(() => {
    fetchAddresses().then((active) => {
      if (active.length > 0 && !address) setAddress(active[0]);
      setAppLoader?.(false);
    });
  }, []);

  if (loading) return <Skeleton active title={false} paragraph={{ rows: 2 }} />;

  const selectedAddr = addresses.find((a) => a?.uuid === address?.uuid);

  return (
    <Card
      size="small"
      style={cardStyle}
      bodyStyle={{ padding: "12px 16px" }}
      onClick={onManageAddress}
    >
      <Row justify="space-between" align="middle">
        <Col>
          <Space size={8}>
            <EnvironmentOutlined style={{ fontSize: 16, color: "#555" }} />
            <span style={{ fontWeight: 600, fontSize: 14 }}>Deliver to</span>
          </Space>
        </Col>
        <Col>
          <Space size={4} align="center">
            <span style={{ fontSize: 13, color: "#333", fontWeight: 500 }}>
              {selectedAddr ? "Change" : "Add address"}
            </span>
            <RightOutlined style={{ fontSize: 11, color: "#999" }} />
          </Space>
        </Col>
      </Row>

      {selectedAddr ? (
        <div style={{ marginTop: 8, paddingLeft: 24 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>
            {selectedAddr.full_name}
            <Tag style={tagStyle}>
              {ADDRESS_TYPE_LABELS[selectedAddr.address_type] || selectedAddr.address_type}
            </Tag>
          </div>
          <div style={{ fontSize: 12, color: "#666", marginTop: 2, lineHeight: 1.5 }}>
            {selectedAddr.address_line1}, {selectedAddr.city},{" "}
            {selectedAddr.state} — {selectedAddr.postal_code || selectedAddr.pincode}
          </div>
          <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>
            +91 {selectedAddr.phone} &nbsp;·&nbsp; {selectedAddr.email}
          </div>
        </div>
      ) : (
        <div style={noAddressText}>No saved addresses</div>
      )}
    </Card>
  );
};

const cardStyle = {
  borderRadius: 10,
  border: "1px solid #e8e8e8",
  cursor: "pointer",
  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
};

const tagStyle = {
  marginLeft: 8,
  fontSize: 10,
  borderRadius: 20,
  border: "1px solid #eee",
  backgroundColor: "#f5f5f5",
  color: "#666",
  padding: "0 6px",
};

const noAddressText = {
  textAlign: "center",
  padding: "10px 0 4px",
  fontSize: 13,
  color: "#aaa",
};

export default SelectAddress;